/**
 * The master mixer — one graph everything in the game plays through.
 *
 *   static  ─┐
 *   signal  ─┤
 *   voice   ─┼─► bus gain ─► sum ─► master gain ─► limiter ─► destination
 *   ghosts  ─┤
 *   ambient ─┘
 *
 * Why buses instead of connecting sources straight to the destination:
 *
 * - **Gain staging.** Each bus carries a fixed trim chosen so that everything playing at once
 *   sums to roughly -12 dBFS. Levels are then set in *relative* terms by the game, and the
 *   absolute headroom stays right no matter how many sources are live.
 * - **One place to duck.** The entry ambience drops under the receiver, ghosts (Ch5) sit under
 *   the story signal, and the player's mute toggles land on a bus rather than being threaded
 *   through every source.
 * - **A limiter that shouldn't work.** The compressor at the end is a safety net for the case
 *   where several transmissions land at once — with correct staging it stays out of the way,
 *   but it guarantees the master never clips into the player's headphones.
 *
 * Every level change is ramped (`rampTo`), never assigned: an AudioParam that jumps produces a
 * step discontinuity, and a step discontinuity is a click.
 */
import { audioContext, rampTo, resumeAudio } from './context.js';

export type BusName = 'static' | 'signal' | 'voice' | 'ghost' | 'ambient';

/**
 * Fixed per-bus trims. These are the gain-staging decisions: `static` is the constant bed so it
 * sits lowest, `signal`/`voice` carry the content the player is trying to hear, `ghost` is
 * deliberately under everything (Ch5 — fragments should feel distant), and `ambient` is the
 * landing-page music bed, muted on every screen past the landing page.
 */
const BUS_TRIM: Record<BusName, number> = {
  static: 0.5,
  signal: 0.85,
  voice: 0.9,
  ghost: 0.45,
  ambient: 0.7,
};

const RAMP_SECONDS = 0.08;
const START_SECONDS = 0.4;

interface Bus {
  /** Where sources connect. */
  input: GainNode;
  /** The fixed staging trim. */
  trim: GainNode;
  level: number;
  muted: boolean;
}

export class Mixer {
  private readonly buses = new Map<BusName, Bus>();
  private readonly sum: GainNode;
  private readonly master: GainNode;
  private readonly limiter: DynamicsCompressorNode;
  private volume: number;

  constructor(volume = 0.7) {
    const ctx = audioContext();
    this.volume = volume;

    this.limiter = ctx.createDynamicsCompressor();
    // Brickwall-ish safety net: high ratio, fast attack, just under 0 dBFS.
    this.limiter.threshold.value = -6;
    this.limiter.knee.value = 3;
    this.limiter.ratio.value = 12;
    this.limiter.attack.value = 0.003;
    this.limiter.release.value = 0.25;
    this.limiter.connect(ctx.destination);

    this.master = ctx.createGain();
    this.master.gain.value = 0; // faded up by start()
    this.master.connect(this.limiter);

    this.sum = ctx.createGain();
    this.sum.gain.value = 1;
    this.sum.connect(this.master);

    for (const [name, trimValue] of Object.entries(BUS_TRIM) as Array<[BusName, number]>) {
      const input = ctx.createGain();
      const trim = ctx.createGain();
      input.gain.value = 1;
      trim.gain.value = trimValue;
      input.connect(trim).connect(this.sum);
      this.buses.set(name, { input, trim, level: 1, muted: false });
    }
  }

  /** Connect a source here. The node is stable for the mixer's lifetime. */
  bus(name: BusName): GainNode {
    return this.buses.get(name)!.input;
  }

  /** Fade the master in. Idempotent; safe to call on every screen change. */
  async start(): Promise<void> {
    await resumeAudio();
    rampTo(this.master.gain, this.volume, START_SECONDS);
  }

  /** Fade the master out without tearing the graph down. */
  stop(seconds = 0.25): void {
    rampTo(this.master.gain, 0, seconds);
  }

  setVolume(value: number, seconds = RAMP_SECONDS): void {
    this.volume = value;
    rampTo(this.master.gain, value, seconds);
  }

  getVolume(): number {
    return this.volume;
  }

  setBusLevel(name: BusName, level: number, seconds = RAMP_SECONDS): void {
    const bus = this.buses.get(name)!;
    bus.level = level;
    if (!bus.muted) rampTo(bus.input.gain, level, seconds);
  }

  getBusLevel(name: BusName): number {
    return this.buses.get(name)!.level;
  }

  setBusMuted(name: BusName, muted: boolean, seconds = RAMP_SECONDS): void {
    const bus = this.buses.get(name)!;
    bus.muted = muted;
    rampTo(bus.input.gain, muted ? 0 : bus.level, seconds);
  }

  /** How hard the safety limiter is working, in dB. Should sit at 0 in normal play. */
  get gainReductionDb(): number {
    return this.limiter.reduction;
  }

  dispose(): void {
    this.stop(0.1);
    setTimeout(() => {
      for (const bus of this.buses.values()) {
        bus.input.disconnect();
        bus.trim.disconnect();
      }
      this.sum.disconnect();
      this.master.disconnect();
      this.limiter.disconnect();
    }, 200);
  }
}
